/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { reduxForm, getFormValues, change, reset } from 'redux-form';
import { ButtonGroup, Button, Page, Layout, Card } from '@shopify/polaris';

import { FullscreenBar } from 'src/components/views/common/fullscreenBar';
import General from 'src/components/views/recommendation/widget/item/general';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { confirmLeavePage } from 'src/helpers';
import {
  checkDisableNextStepFromGeneral,
  buildWidgetValues,
  buildInitialWidgetValues
} from 'src/helpers/recommendation-helper';
import { hasPermissionRecommendation } from 'src/helpers/permission-helper';
import history from 'src/routes/history';

import {
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES as RECOMMENDATION_TYPES,
  AIML_QUERY_INTERVAL_MILIS as INTERVAL_QUERY
} from 'src/constants/recommendation';

import * as recommendationActions from 'actions/recommendation-actions';

import type { ComposedProps, State } from './index.type';

class GeneralWidgetItem extends Component<ComposedProps, State> {
  querySyncInterval = undefined;

  state = {
    aimlPermission: false
  };

  componentDidMount() {
    const { getSyncOrderStatus, syncOrderStatus, accessScopes } = this.props;

    if (hasPermissionRecommendation(accessScopes)) {
      this.setState({ aimlPermission: true });
    }

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);

    if (syncOrderStatus.current?.status !== 'done') getSyncOrderStatus();
  }

  componentWillReceiveProps(nextProps) {
    const { aimlPermission } = this.state;
    const { widget, syncOrderStatus, getSyncOrderStatus } = nextProps;

    const recommendationType = _.get(widget, 'recommendationType', '');
    const useAIWidget =
      recommendationType === RECOMMENDATION_TYPES['related-items'].value ||
      recommendationType === RECOMMENDATION_TYPES['frequently-bought-together'].value ||
      recommendationType === RECOMMENDATION_TYPES['recently-purchased'].value;

    if (aimlPermission && useAIWidget) {
      if (syncOrderStatus.current?.status === 'running' && !this.querySyncInterval) {
        this.querySyncInterval = setInterval(() => {
          getSyncOrderStatus();
        }, INTERVAL_QUERY);
      }

      if (syncOrderStatus.current?.status === 'done') {
        if (this.querySyncInterval) clearInterval(this.querySyncInterval);
      }
    }
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);

    if (this.querySyncInterval) clearInterval(this.querySyncInterval);
  }

  render() {
    const {
      i18n,
      formValues,
      widget,
      host,
      updateField,
      syncOrderStatus,
      handleSubmit,
      dirty,
      dirtyOnStep,
      currentStep,
      isCreateFlow,
      handleCloseFullscreenPage
    } = this.props;

    const { aimlPermission } = this.state;
    const disabledNextStep = this.checkDisableNext();
    const shouldConfirmWhenLeave =
      !_.isEmpty(_.filter(dirtyOnStep, (value, key) => key !== `step_${currentStep}` && value)) ||
      dirty;

    const title = isCreateFlow ? `NewWidgetItem.step2Title` : `WidgetItem.title`;
    return (
      <div className="BC-Page BC-Page__RecommendationNewWidgetItem BC-Page__RecommendationWidgetItem">
        <FullscreenBar
          onAction={() => handleCloseFullscreenPage(!shouldConfirmWhenLeave)}
          backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
          title={i18n.translate(`AdminHTML.Recommendation.${title}`)}
          actions={
            <ButtonGroup>
              {isCreateFlow ? (
                <Button onClick={this.handlePrevious}>
                  {i18n.translate('AdminHTML.Common.Action.previous')}
                </Button>
              ) : (
                ''
              )}

              <Button
                primary
                onClick={handleSubmit(this.handleNext.bind(this))}
                disabled={disabledNextStep}>
                {i18n.translate('AdminHTML.Common.Action.next')}
              </Button>
            </ButtonGroup>
          }
          progress={isCreateFlow ? 50 : -1}
        />
        <div style={{ marginTop: '2rem' }}>
          <Page>
            <Layout>
              <Layout.Section>
                <Card sectioned>
                  <div className="setup-content">
                    <General
                      formValues={formValues}
                      recommendationWidget={widget}
                      host={host}
                      updateField={updateField}
                      aimlPermission={aimlPermission}
                      syncOrderStatus={syncOrderStatus.current}
                    />
                  </div>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        </div>
        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={shouldConfirmWhenLeave}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={shouldConfirmWhenLeave}
          isAvailableFullscreen={true}
        />
      </div>
    );
  }

  // Handle action previous
  handlePrevious = () => {
    let { widget, updateState, currentStep, formValues, dirty, dirtyOnStep } = this.props;
    let recommendationWidget = buildWidgetValues(formValues);
    widget = { ...widget, ...recommendationWidget };
    dirtyOnStep[`step_${currentStep}`] = dirty;

    updateState('dirtyOnStep', dirtyOnStep);
    updateState('widget', widget);
    updateState('currentStep', currentStep - 1);
  };

  // Check disable next button
  checkDisableNext = () => {
    const { formValues, accessScopes } = this.props;
    const aimlPermission = hasPermissionRecommendation(accessScopes);
    const data = { aimlPermission, formValues };

    return checkDisableNextStepFromGeneral(data);
  };

  // Handle action next
  handleNext = data => {
    let { widget, updateState, currentStep } = this.props;
    let recommendationWidget = buildWidgetValues(data);
    widget = { ...widget, ...recommendationWidget };

    updateState('widget', widget);
    updateState('currentStep', currentStep + 1);
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { i18n, dirty } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (id, data) => dispatch(change(`RecommendationGeneralWidgetItemForm`, id, data)),
  getSyncOrderStatus: () => dispatch(recommendationActions.getSyncOrderStatus(ownProps.i18n)),
  resetForm: () => dispatch(reset('RecommendationGeneralWidgetItemForm'))
});

const mapStateToProps = (state, props) => {
  const { widget } = props;
  const syncOrderStatus = _.get(state.recommendation, 'syncOrderStatus', {});
  const formValues = getFormValues('RecommendationGeneralWidgetItemForm')(state);

  // Get access scopes
  const accessScopes = _.get(state.oauth, 'tenantApp.accessScopes', {});

  return {
    initialValues: buildInitialWidgetValues(widget),
    formValues: formValues || {},
    host: _.get(state.oauth, 'host', ''),
    syncOrderStatus,
    accessScopes
  };
};

const GeneralWidgetItemForm = reduxForm({
  form: 'RecommendationGeneralWidgetItemForm', // a unique identifier for this form
  enableReinitialize: true
})(GeneralWidgetItem);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(GeneralWidgetItemForm));
